import { retainCalculatedShading } from './canvas-transitions';

export interface CanvasChangeStatus {
  isCanvasChanged: boolean;
  isDesignChanged: boolean;
  isAnnotationChanged: boolean;
}

const _sceneNumber = (val: any) => {
  if (val == null || val === '') return null;
  const n = Number(val);
  return isNaN(n) ? null : Number(n.toFixed(2));
};

const _normalizeNumeric = (val: any): any => {
  if (Array.isArray(val)) return val.map(_normalizeNumeric);
  if (typeof val === 'number') return _sceneNumber(val);
  if (typeof val === 'string') {
    if (val.includes(',')) return val.split(',').map(_normalizeNumeric);
    if (val.trim() !== '' && !isNaN(Number(val))) return _sceneNumber(val);
  }
  return val;
};

const _normalizeDesignValues = (design: Record<string, any>) =>
  Object.fromEntries(
    Object.entries(design).map(([key, val]) => [key, _normalizeNumeric(val)])
  );

const _selectionSignature = (selected: any): number[] => {
  if (typeof selected === 'string') {
    const text = selected.trim();
    if (!text) return [];
    return _selectionSignature(text.split(','));
  }
  if (!Array.isArray(selected) || selected.length === 0) return [];
  const flagged = selected.map(
    (value) => value === true || value === 1 || value === '1'
  );
  if (flagged.some(Boolean)) {
    return flagged.flatMap((on, index) => (on ? [index] : []));
  }
  const indexes = selected.filter(
    (value) => typeof value === 'number' && value > 1
  );
  return indexes.length > 0
    ? indexes.map((value) => Number(value)).sort((a, b) => a - b)
    : [];
};

const _pointSignature = (point: any) => {
  const normalized = _normalizeNumeric(point);
  return Array.isArray(normalized) ? normalized.slice(0, 2) : normalized;
};

const _panelSideSignature = (side: any) => ({
  points: (Array.isArray(side?.points) ? side.points : []).map(_pointSignature),
  selected: _selectionSignature(side?.selected)
});

const _panelsSignature = (panels: any) => {
  if (!Array.isArray(panels)) return _normalizeNumeric(panels);
  return panels.map((panel) => {
    if (!panel || typeof panel !== 'object') return panel;
    if (!panel.horizontal && !panel.vertical) {
      return {
        id: panel.id == null ? null : String(panel.id),
        points: _normalizeNumeric(panel.points ?? []),
        selected: _selectionSignature(panel.selected)
      };
    }
    return {
      id: panel.id == null ? null : String(panel.id),
      horizontal: _panelSideSignature(panel.horizontal),
      vertical: _panelSideSignature(panel.vertical)
    };
  });
};

const _obstacleRadius = (obstacle: any) => {
  const shape = String(obstacle?.shape || '');
  if (shape === 'Polygon' || shape === 'Line') return null;
  return _sceneNumber(obstacle?.radius);
};

const _coordKey = (geom: any) => {
  const coords = geom?.coords || geom;
  if (!Array.isArray(coords)) return String(geom);
  return JSON.stringify(
    coords.map((c: any) => {
      if (Array.isArray(c) && c.length >= 2)
        return [_sceneNumber(c[0]), _sceneNumber(c[1])];
      return String(c);
    })
  );
};

const _toPoint = (point: any): any => {
  if (typeof point === 'string') return point.split(',').map(_sceneNumber);
  if (Array.isArray(point)) return point.map(_sceneNumber);
  return _sceneNumber(point);
};

const _normalizeCoords = (geom: any): any[] => {
  let coords: any =
    geom && typeof geom === 'object' && !Array.isArray(geom)
      ? geom.coords
      : geom;
  if (typeof coords === 'string') {
    try {
      coords = JSON.parse(coords);
    } catch {
      coords = coords.split(';');
    }
  }
  if (coords == null) return [];
  if (!Array.isArray(coords)) return [_toPoint(coords)];
  let points = coords.map(_toPoint);
  if (
    points.length > 1 &&
    JSON.stringify(points[0]) === JSON.stringify(points[points.length - 1])
  ) {
    points = points.slice(0, -1);
  }
  return points;
};

const _normalizeLines = (lines: any): string[] => {
  if (lines == null) return [];
  const parts: any[] =
    typeof lines === 'string'
      ? lines === ''
        ? []
        : lines.split(',')
      : Array.isArray(lines)
        ? lines
        : [lines];
  return parts.map((p) => {
    const text = String(p ?? '').trim();
    return text === 'None' || text === 'none' ? '' : text;
  });
};

const _normalizeSetbacks = (setbacks: any): any[] => {
  if (setbacks == null || setbacks === '') return [];
  const normalized = _normalizeNumeric(setbacks);
  return Array.isArray(normalized) ? normalized : [normalized];
};

const _segmentCanvasRecord = (s: any) => ({
  pitch: _sceneNumber(s.pitch),
  azimuth: _sceneNumber(s.azimuth),
  height: _sceneNumber(s.height),
  geometry: _normalizeCoords(s.geometry),
  lines: _normalizeLines(s.lines),
  setbacks: _normalizeSetbacks(s.setbacks)
});

const SEGMENT_CANVAS_KEYS = [
  'pitch',
  'azimuth',
  'height',
  'geometry',
  'lines',
  'setbacks'
] as const;

const _segmentCanvasSignatures = (segments: any) =>
  (segments || [])
    .map((s: any) => JSON.stringify(_segmentCanvasRecord(s)))
    .sort();

const _truncateForLog = (value: unknown, max = 160): string => {
  const text = JSON.stringify(value) ?? String(value);
  return text.length > max ? `${text.slice(0, max)}...` : text;
};

const _segmentCanvasDiffs = (base: any, current: any) => {
  const baseById = new Map<string, any>(
    (base.segments || []).map((s: any) => {
      const record = _segmentCanvasRecord(s);
      return [String(s.id ?? ''), record];
    })
  );
  const currById = new Map<string, any>(
    (current.segments || []).map((s: any) => {
      const record = _segmentCanvasRecord(s);
      return [String(s.id ?? ''), record];
    })
  );
  const diffs: Record<string, any>[] = [];
  for (const [id, curr] of currById) {
    const prev = baseById.get(id);
    if (!prev) {
      diffs.push({ id, change: 'added' });
      continue;
    }
    const keys = SEGMENT_CANVAS_KEYS.filter(
      (key) => JSON.stringify(prev[key]) !== JSON.stringify(curr[key])
    );
    if (keys.length > 0) {
      diffs.push({
        id,
        change: 'changed',
        keys,
        before: keys.map((key) => _truncateForLog(prev[key])),
        after: keys.map((key) => _truncateForLog(curr[key]))
      });
    }
  }
  for (const id of baseById.keys()) {
    if (!currById.has(id)) diffs.push({ id, change: 'removed' });
  }
  return diffs;
};

const _rawSegmentFieldDiffs = (base: any, current: any) => {
  const baseById = new Map<string, any>(
    (base.segments || []).map((s: any) => [String(s.id ?? ''), s])
  );
  const diffs: Record<string, any>[] = [];
  for (const segment of current.segments || []) {
    const prev = baseById.get(String(segment.id ?? ''));
    if (!prev) continue;
    const keys = Array.from(
      new Set([...Object.keys(prev), ...Object.keys(segment)])
    ).filter(
      (key) => JSON.stringify(prev[key]) !== JSON.stringify(segment[key])
    );
    if (keys.length > 0) {
      diffs.push({
        id: String(segment.id ?? ''),
        keys: keys.slice(0, 15),
        before: keys.slice(0, 15).map((key) => _truncateForLog(prev[key])),
        after: keys.slice(0, 15).map((key) => _truncateForLog(segment[key]))
      });
    }
    if (diffs.length >= 3) break;
  }
  return diffs;
};

const _panelSignatureDiffs = (base: any, current: any) => {
  const before = _panelsSignature(base.panels);
  const after = _panelsSignature(current.panels);
  if (!Array.isArray(before) || !Array.isArray(after)) {
    return JSON.stringify(before) === JSON.stringify(after)
      ? []
      : [{ before: _truncateForLog(before), after: _truncateForLog(after) }];
  }
  const diffs: Record<string, any>[] = [];
  const length = Math.max(before.length, after.length);
  for (let index = 0; index < length && diffs.length < 5; index++) {
    const prev = before[index];
    const next = after[index];
    if (JSON.stringify(prev) === JSON.stringify(next)) continue;
    const nested = Boolean(
      prev?.horizontal || prev?.vertical || next?.horizontal || next?.vertical
    );
    const paths = (nested ? ['horizontal', 'vertical'] : ['']).flatMap((side) =>
      ['points', 'selected'].map((key) => (side ? `${side}.${key}` : key))
    );
    const read = (panel: any, path: string) =>
      path.split('.').reduce((value, key) => value?.[key], panel) || [];
    const fields = paths
      .filter(
        (path) =>
          JSON.stringify(read(prev, path)) !== JSON.stringify(read(next, path))
      )
      .map((path) => {
        const a = read(prev, path);
        const b = read(next, path);
        let at = 0;
        while (
          at < Math.max(a.length, b.length) &&
          JSON.stringify(a[at]) === JSON.stringify(b[at])
        ) {
          at++;
        }
        return {
          path,
          lengthBefore: a.length,
          lengthAfter: b.length,
          at,
          before: _truncateForLog(a[at]),
          after: _truncateForLog(b[at])
        };
      });
    diffs.push({
      index,
      id: next?.id ?? prev?.id ?? null,
      idChanged: prev?.id !== next?.id,
      fields
    });
  }
  return diffs;
};

const _rawTopLevelKeyDiffs = (base: any, current: any) => {
  const keys = Array.from(
    new Set([...Object.keys(base), ...Object.keys(current)])
  ).filter(
    (key) =>
      key !== 'segments' &&
      JSON.stringify(base[key]) !== JSON.stringify(current[key])
  );
  return keys.slice(0, 25).map((key) => ({
    key,
    before: _truncateForLog(base[key]),
    after: _truncateForLog(current[key])
  }));
};

const _segmentDesignValues = (s: any) => ({
  solar_access: _normalizeNumeric(s.solarAccess ?? s.solar_access ?? null),
  tsrf: _normalizeNumeric(s.tsrf ?? null),
  tof: _normalizeNumeric(s.tof ?? null),
  ac_monthly: _normalizeNumeric(s.acMonthly ?? s.ac_monthly ?? null),
  poa_monthly: _normalizeNumeric(s.poaMonthly ?? s.poa_monthly ?? null),
  annual_kwh: _normalizeNumeric(
    s.annual_kwh ?? s.annualKwh ?? s.acAnnual ?? null
  ),
  clipping_kwh: _normalizeNumeric(s.clipping_kwh ?? s.clippingKwh ?? null),
  monthly_clipping_kwh: _normalizeNumeric(
    s.monthly_clipping_kwh ?? s.monthlyClippingKwh ?? null
  ),
  stringing_data: _normalizeNumeric(
    s.stringing_data ?? s.stringingData ?? null
  ),
  selected_panel_count: _normalizeNumeric(
    s.selected_panel_count ?? s.selectedPanelCount ?? null
  )
});

const _segmentDesignDiffs = (base: any, current: any) => {
  const baseById = new Map<string, any>(
    (base.segments || []).map((s: any) => [String(s.id), s])
  );
  const diffs: { id: string; keys: string[] }[] = [];
  for (const segment of current.segments || []) {
    const previous = baseById.get(String(segment.id));
    if (!previous) continue;
    const before = _segmentDesignValues(previous);
    const after = _segmentDesignValues(segment);
    const keys = Object.keys(after).filter((key) => {
      const previousValue = (before as any)[key];
      const nextValue = (after as any)[key];
      if (JSON.stringify(previousValue) === JSON.stringify(nextValue)) {
        return false;
      }
      if (key === 'solar_access' || key === 'tsrf' || key === 'tof') {
        return (
          JSON.stringify(retainCalculatedShading(previousValue, nextValue)) !==
          JSON.stringify(previousValue)
        );
      }
      return true;
    });
    if (keys.length > 0) diffs.push({ id: String(segment.id), keys });
  }
  return diffs;
};

const HARDWARE_DESIGN_KEYS = new Set([
  'selected_panel_id',
  'selected_panel_spec',
  'selected_inverter_id',
  'selected_inverter_spec',
  'inverter_quantity',
  'primary_inverter_auto_quantity',
  'additional_inverters'
]);

const _hasPlacedPanels = (state: any): boolean => {
  const panels = state?.panels;
  if (Array.isArray(panels)) return panels.length > 0;
  return panels != null && typeof panels === 'object'
    ? Object.keys(panels).length > 0
    : false;
};

export const UNSAVED_CHANGES_GUARD_MESSAGE =
  'You have unsaved changes. If you leave or switch, your changes will be lost.';

export function isDirtyStatus(status: CanvasChangeStatus): boolean {
  return Boolean(
    status &&
    (status.isCanvasChanged ||
      status.isDesignChanged ||
      status.isAnnotationChanged)
  );
}

const CANVAS_PAYLOAD_KEYS = [
  'segments',
  'obstacles',
  'trees',
  'color_range',
  'colorRange',
  'shading_images'
];

const DESIGN_PAYLOAD_KEYS = [
  'panels',
  'stringing_data',
  'stringingData',
  'stringing_roof_index_map',
  'stringing_selected_panel_index_map',
  'stringing_panel_index_mode',
  'total_annual_kwh',
  'total_monthly_kwh',
  'total_clipping_kwh',
  'total_monthly_clipping_kwh',
  'dc_ac_ratio',
  'system_size_kw',
  'mppt_count_total',
  'inverter_count_total',
  'inverters_used',
  'production_per_panel',
  'production_per_string',
  'hourly_production',
  'max_dc_ac_ratio',
  'only_selected_panels',
  'inverter_quantity',
  'primary_inverter_auto_quantity',
  'additional_inverters',
  'selected_panel_id',
  'selected_panel_spec',
  'selected_inverter_id',
  'selected_inverter_spec',
  'selected_panel_count',
  'system_losses_state',
  'losses',
  'offset_percentage',
  'annual_usage_kwh',
  'use_target_production',
  'target_production_kwh',
  'buffer',
  'row_spacing',
  'panel_tilt',
  'azimuth_restriction',
  'panel_placement_fill_mode',
  'panelPlacementFillMode',
  'panel_placement_layout_mode',
  'panelPlacementLayoutMode',
  'panel_placement_only_selected_roof',
  'package'
];

export function changePayloadForSave(
  status: CanvasChangeStatus,
  current: any
): Record<string, any> {
  const out: Record<string, any> = {};
  if (!current || typeof current !== 'object') return out;
  if (status.isCanvasChanged) {
    for (const key of CANVAS_PAYLOAD_KEYS) {
      if (current[key] !== undefined) out[key] = current[key];
    }
  }
  if (status.isDesignChanged) {
    for (const key of DESIGN_PAYLOAD_KEYS) {
      if (current[key] !== undefined) out[key] = current[key];
    }
    if (current.segments !== undefined) out.segments = current.segments;
  }
  if (status.isAnnotationChanged) {
    if (current.annotations !== undefined)
      out.annotations = current.annotations;
    if (current.checklist !== undefined) out.checklist = current.checklist;
  }
  return out;
}

export function diffCanvasStates(base: any, current: any): CanvasChangeStatus {
  if (!base) base = {};
  if (!current) current = {};

  const baseSegments = _segmentCanvasSignatures(base.segments);
  const currSegments = _segmentCanvasSignatures(current.segments);

  const baseObstacles = (base.obstacles || [])
    .map((o: any) =>
      JSON.stringify({
        geometry: _coordKey(o.geometry),
        height: _sceneNumber(o.height),
        radius: _obstacleRadius(o),
        setback: _sceneNumber(o.setback)
      })
    )
    .sort();
  const currObstacles = (current.obstacles || [])
    .map((o: any) =>
      JSON.stringify({
        geometry: _coordKey(o.geometry),
        height: _sceneNumber(o.height),
        radius: _obstacleRadius(o),
        setback: _sceneNumber(o.setback)
      })
    )
    .sort();

  const baseTrees = (base.trees || [])
    .map((t: any) =>
      JSON.stringify({
        geometry: _coordKey(t.geometry || t.position),
        height: _sceneNumber(t.height),
        trunk_height: _sceneNumber(t.trunkHeight || t.trunk_height),
        radius: _sceneNumber(t.radius)
      })
    )
    .sort();
  const currTrees = (current.trees || [])
    .map((t: any) =>
      JSON.stringify({
        geometry: _coordKey(t.geometry || t.position),
        height: _sceneNumber(t.height),
        trunk_height: _sceneNumber(t.trunkHeight || t.trunk_height),
        radius: _sceneNumber(t.radius)
      })
    )
    .sort();

  const canvasDiffReasons: string[] = [];
  if (JSON.stringify(baseSegments) !== JSON.stringify(currSegments)) {
    canvasDiffReasons.push('segments');
  }
  if (JSON.stringify(baseObstacles) !== JSON.stringify(currObstacles)) {
    canvasDiffReasons.push('obstacles');
  }
  if (JSON.stringify(baseTrees) !== JSON.stringify(currTrees)) {
    canvasDiffReasons.push('trees');
  }
  if (
    String(base.colorRange || base.color_range) !==
    String(current.colorRange || current.color_range)
  ) {
    canvasDiffReasons.push('color_range');
  }
  if (
    String(base.shading_images || '') !== String(current.shading_images || '')
  ) {
    canvasDiffReasons.push('shading_images');
  }
  const isCanvasChanged = canvasDiffReasons.length > 0;

  const baseDesign = {
    selected_panel_id:
      base.selected_panel_id || base.package?.selected_panel_id,
    selected_panel_spec:
      base.selected_panel_spec || base.package?.selected_panel_spec,
    selected_inverter_id:
      base.selected_inverter_id || base.package?.selected_inverter_id,
    selected_inverter_spec:
      base.selected_inverter_spec || base.package?.selected_inverter_spec,
    inverter_quantity:
      base.inverter_quantity ||
      base.package?.inverter_quantity ||
      base.package?.inverterQuantity,
    primary_inverter_auto_quantity:
      base.primary_inverter_auto_quantity ??
      base.package?.primary_inverter_auto_quantity,
    additional_inverters:
      base.additional_inverters ||
      base.package?.additional_inverters ||
      base.package?.additionalInverters,
    panels: _panelsSignature(base.panels),
    stringing_data: base.stringing_data || base.stringingData,
    stringing_roof_index_map: base.stringing_roof_index_map,
    stringing_panel_index_mode: base.stringing_panel_index_mode,
    stringing_selected_panel_index_map: base.stringing_selected_panel_index_map,
    total_annual_kwh: base.total_annual_kwh,
    total_monthly_kwh: base.total_monthly_kwh,
    total_clipping_kwh: base.total_clipping_kwh,
    total_monthly_clipping_kwh: base.total_monthly_clipping_kwh,
    dc_ac_ratio: base.dc_ac_ratio,
    system_size_kw: base.system_size_kw,
    mppt_count_total: base.mppt_count_total,
    inverter_count_total: base.inverter_count_total,
    inverters_used: base.inverters_used,
    production_per_panel: base.production_per_panel,
    production_per_string: base.production_per_string,
    hourly_production: base.hourly_production,
    max_dc_ac_ratio: base.max_dc_ac_ratio,
    only_selected_panels: base.only_selected_panels,
    selected_panel_count: base.selected_panel_count,
    losses: base.losses,
    system_losses_state: base.system_losses_state,
    offset_percentage: base.offset_percentage ?? base.offsetPercent,
    annual_usage_kwh: base.annual_usage_kwh,
    use_target_production: base.use_target_production,
    target_production_kwh: base.target_production_kwh,
    buffer: base.buffer,
    row_spacing: base.row_spacing ?? base.rowSpacing,
    panel_tilt: base.panel_tilt ?? base.panelTilt,
    azimuth_restriction: base.azimuth_restriction ?? base.azimuthRestriction,
    panel_placement_fill_mode:
      base.panel_placement_fill_mode || base.panelPlacementFillMode,
    panel_placement_layout_mode:
      base.panel_placement_layout_mode || base.panelPlacementLayoutMode,
    panel_placement_only_selected_roof:
      base.panel_placement_only_selected_roof ??
      base.panelPlacementOnlySelectedRoof
  };
  const currDesign = {
    selected_panel_id:
      current.selected_panel_id || current.package?.selected_panel_id,
    selected_panel_spec:
      current.selected_panel_spec || current.package?.selected_panel_spec,
    selected_inverter_id:
      current.selected_inverter_id || current.package?.selected_inverter_id,
    selected_inverter_spec:
      current.selected_inverter_spec || current.package?.selected_inverter_spec,
    inverter_quantity:
      current.inverter_quantity ||
      current.package?.inverter_quantity ||
      current.package?.inverterQuantity,
    primary_inverter_auto_quantity:
      current.primary_inverter_auto_quantity ??
      current.package?.primary_inverter_auto_quantity,
    additional_inverters:
      current.additional_inverters ||
      current.package?.additional_inverters ||
      current.package?.additionalInverters,
    panels: _panelsSignature(current.panels),
    stringing_data: current.stringing_data || current.stringingData,
    stringing_roof_index_map: current.stringing_roof_index_map,
    stringing_panel_index_mode: current.stringing_panel_index_mode,
    stringing_selected_panel_index_map:
      current.stringing_selected_panel_index_map,
    total_annual_kwh: current.total_annual_kwh,
    total_monthly_kwh: current.total_monthly_kwh,
    total_clipping_kwh: current.total_clipping_kwh,
    total_monthly_clipping_kwh: current.total_monthly_clipping_kwh,
    dc_ac_ratio: current.dc_ac_ratio,
    system_size_kw: current.system_size_kw,
    mppt_count_total: current.mppt_count_total,
    inverter_count_total: current.inverter_count_total,
    inverters_used: current.inverters_used,
    production_per_panel: current.production_per_panel,
    production_per_string: current.production_per_string,
    hourly_production: current.hourly_production,
    max_dc_ac_ratio: current.max_dc_ac_ratio,
    only_selected_panels: current.only_selected_panels,
    selected_panel_count: current.selected_panel_count,
    losses: current.losses,
    system_losses_state: current.system_losses_state,
    offset_percentage: current.offset_percentage ?? current.offsetPercent,
    annual_usage_kwh: current.annual_usage_kwh,
    use_target_production: current.use_target_production,
    target_production_kwh: current.target_production_kwh,
    buffer: current.buffer,
    row_spacing: current.row_spacing ?? current.rowSpacing,
    panel_tilt: current.panel_tilt ?? current.panelTilt,
    azimuth_restriction:
      current.azimuth_restriction ?? current.azimuthRestriction,
    panel_placement_fill_mode:
      current.panel_placement_fill_mode || current.panelPlacementFillMode,
    panel_placement_layout_mode:
      current.panel_placement_layout_mode || current.panelPlacementLayoutMode,
    panel_placement_only_selected_roof:
      current.panel_placement_only_selected_roof ??
      current.panelPlacementOnlySelectedRoof
  };

  const baseDesignNormalized = _normalizeDesignValues(baseDesign);
  const currDesignNormalized = _normalizeDesignValues(currDesign);
  const hardwareRelevant = _hasPlacedPanels(base) || _hasPlacedPanels(current);
  const isIgnoredDesignKey = (key: string) =>
    !hardwareRelevant && HARDWARE_DESIGN_KEYS.has(key);
  const designDiffReasons: string[] = [];
  for (const key of Object.keys(currDesignNormalized)) {
    if (isIgnoredDesignKey(key)) continue;
    if (
      JSON.stringify(baseDesignNormalized[key]) !==
      JSON.stringify(currDesignNormalized[key])
    ) {
      designDiffReasons.push(key);
    }
  }
  for (const key of Object.keys(baseDesignNormalized)) {
    if (isIgnoredDesignKey(key)) continue;
    if (
      !(key in currDesignNormalized) &&
      !designDiffReasons.includes(key) &&
      JSON.stringify(baseDesignNormalized[key]) !==
        JSON.stringify(currDesignNormalized[key])
    ) {
      designDiffReasons.push(key);
    }
  }
  const segmentDesignDiffs = _segmentDesignDiffs(base, current);
  if (segmentDesignDiffs.length > 0) designDiffReasons.push('segment_design');
  const isDesignChanged = designDiffReasons.length > 0;

  const baseAnnotations = (base.annotations || [])
    .map((a: any) => JSON.stringify(a))
    .sort();
  const currAnnotations = (current.annotations || [])
    .map((a: any) => JSON.stringify(a))
    .sort();
  const isAnnotationChanged =
    JSON.stringify(baseAnnotations) !== JSON.stringify(currAnnotations);
  const isCanvasChangedWithAnnotations = isCanvasChanged || isAnnotationChanged;

  if (
    isCanvasChangedWithAnnotations ||
    isDesignChanged ||
    isAnnotationChanged
  ) {
    const first =
      canvasDiffReasons[0] ||
      (isAnnotationChanged ? 'annotations' : undefined) ||
      designDiffReasons[0];
    console.log('[designer-change-detection]', {
      isCanvasChanged: isCanvasChangedWithAnnotations,
      isDesignChanged,
      isAnnotationChanged,
      firstDifferingField: first,
      canvasDiffReasons,
      designDiffReasons,
      segmentDesignDiffs: segmentDesignDiffs.slice(0, 5),
      segmentCanvasDiffs: _segmentCanvasDiffs(base, current).slice(0, 10),
      panelSignatureDiffs: designDiffReasons.includes('panels')
        ? _panelSignatureDiffs(base, current)
        : [],
      rawSegmentFieldDiffs: _rawSegmentFieldDiffs(base, current),
      rawTopLevelKeyDiffs: _rawTopLevelKeyDiffs(base, current),
      baseSegmentCount: (base.segments || []).length,
      currentSegmentCount: (current.segments || []).length
    });
  }

  return {
    isCanvasChanged: isCanvasChangedWithAnnotations,
    isDesignChanged,
    isAnnotationChanged
  };
}
